import { TabProps } from '../Tab'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { twMerge } from 'tailwind-merge'
import { motion } from 'framer-motion'
import { useId, useLayoutEffect, useRef, useState } from 'react'
import { Select } from '../../Form/Select/Select'

export type StepTitleProps = {
  type: 'complete' | 'current' | 'upcoming' | 'error'
  title: string
  stepNumber: number
  isLast?: boolean
  onClick: () => void
}

type Props = {
  currentStep: number
  routing?: boolean
  className?: string
  tabTitleClasses?: string
  stepPropsList: TabProps[]
  onClickTitle: (stepIndex: number) => void
}
export const TabHeader = (props: Props) => {
  const isMobile = useIsMobile()
  const navRef = useRef<HTMLElement>(null)
  const [overflows, setOverflows] = useState(false)
  const componentId = useId()
  const currentStep = props.stepPropsList[props.currentStep]

  useLayoutEffect(() => {
    if (!isMobile) {
      setOverflows(false)
      return
    }
    if (!navRef.current) return
    setOverflows(navRef.current.scrollWidth > navRef.current.clientWidth)
  }, [props.stepPropsList, isMobile])

  const navContainerClasses = twMerge(
    'flex items-stretch justify-between sm:justify-start sm:px-0 h-full w-full',
    props.className,
  )

  if (isMobile && overflows) {
    return (
      <header className='w-full px-2 pt-2 pb-2 border-b'>
        <Select
          key={props.currentStep}
          name='tab-select'
          label={currentStep?.title !== '' ? 'Viewing' : ''}
          variant='prefixed-dropdown'
          noLabel
          required
          defaultValues={props.currentStep}
          onSelect={(index) => props.onClickTitle(index as number)}
          className='mt-0'>
          {props.stepPropsList.map((tab, i) => (
            <Select.Option key={tab.title} label={tab.title} value={i} />
          ))}
        </Select>
      </header>
    )
  }

  return (
    <motion.header
      layoutRoot
      layout
      className='relative w-full flex flex-col grow-0 shrink-0 overflow-hidden '>
      <nav
        ref={navRef}
        role='tablist'
        className={navContainerClasses}
        aria-label='Tabs'>
        {props.stepPropsList.map((tab, i) => {
          const isSelected = props.currentStep === i
          return (
            <button
              key={tab.title}
              type='button'
              role='tab'
              aria-selected={isSelected}
              onClick={() => props.onClickTitle(i)}
              className={twMerge(
                'relative flex justify-center items-center transition w-full sm:w-fit',
                'sm:px-6 py-4 px-1',
                'text-base-900 hover:text-base-700 font-medium',
                isSelected &&
                  'dark:text-primary-700 text-primary-800 hover:text-primary-700 dark:bg-primary-600/10 bg-primary-500/25 border-transparent',
              )}
              aria-current={props.currentStep === i ? 'page' : undefined}>
              <p className={twMerge(props.tabTitleClasses)}>{tab.title}</p>
              {isSelected && (
                <motion.div
                  layoutId={`tab-button-${componentId}`}
                  className='absolute -bottom-px left-0 w-full h-0.5 bg-primary-600'
                />
              )}
            </button>
          )
        })}
      </nav>
      <div className='w-full h-px bg-base-300 shrink-0' />
    </motion.header>
  )
}
